import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  Fade,
  Skeleton,
} from '@mui/material';
import { AutoStories } from '@mui/icons-material';

function StoryScene({ scene, onChoiceSelect, isLoading, sceneNumber, totalScenes }) {
  if (!scene) {
    return null;
  }

  return (
    <Fade in timeout={800}>
      <Box>
        {/* Scene Image */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            overflow: 'hidden',
            border: '3px solid #8D7B68',
            boxShadow: '0 8px 16px rgba(141, 123, 104, 0.3)',
          }}
        >
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={400}
              animation="wave"
            />
          ) : (
            <CardMedia
              component="img"
              image={scene.imageUrl}
              alt={`Scene ${scene.sceneNumber}`}
              sx={{
                width: '100%',
                height: { xs: 300, sm: 400, md: 500 },
                objectFit: 'cover',
              }}
            />
          )}
        </Card>

        {/* Scene Progress */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <AutoStories sx={{ color: '#8D7B68', mr: 1, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#8D7B68', fontWeight: 600 }}>
            Chapter {sceneNumber} of Your Tale
          </Typography>
        </Box>

        {/* Scene Text */}
        <Box
          sx={{
            backgroundColor: '#F7EEDC',
            border: '2px solid #8D7B68',
            borderRadius: 3,
            p: { xs: 3, sm: 4, md: 5 },
            mb: 4,
            position: 'relative',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#2C2416',
              lineHeight: 2,
              fontSize: { xs: '1.05rem', sm: '1.15rem' },
              whiteSpace: 'pre-line',
              textAlign: 'justify',
            }}
          >
            {scene.text}
          </Typography>
        </Box>

        {/* Choices */}
        {scene.choices && scene.choices.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: '#8D7B68',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
              }}
            >
              What shall Your Majesty choose?
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {scene.choices.map((choice, index) => (
                <Fade in timeout={1000 + index * 200} key={index}>
                  <Button
                    variant="outlined"
                    onClick={() => onChoiceSelect(choice)}
                    disabled={isLoading}
                    sx={{
                      p: 3,
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      borderColor: '#8D7B68',
                      borderWidth: 2,
                      color: '#333',
                      backgroundColor: '#FFF',
                      borderRadius: 2,
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#8D7B68',
                        color: '#FFF',
                        borderColor: '#8D7B68',
                        transform: 'translateX(8px)',
                        boxShadow: '0 4px 12px rgba(141, 123, 104, 0.3)',
                      },
                      '&:disabled': {
                        borderColor: '#CCC',
                        color: '#999',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          mr: 2,
                          color: 'inherit',
                        }}
                      >
                        {String.fromCharCode(65 + index)}.
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '1.05rem' },
                          lineHeight: 1.6,
                          color: 'inherit',
                        }}
                      >
                        {choice}
                      </Typography>
                    </Box>
                  </Button>
                </Fade>
              ))}
            </Box>
          </Box>
        )}

        {/* Story Complete Message */}
        {scene.choices && scene.choices.length === 0 && (
          <Fade in timeout={1000}>
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                px: 3,
                backgroundColor: '#F7EEDC',
                border: '2px solid #8D7B68',
                borderRadius: 3,
              }}
            >
              <AutoStories sx={{ fontSize: 48, color: '#8D7B68', mb: 2 }} />
              <Typography
                variant="h5"
                sx={{
                  color: '#8D7B68',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                The End
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', fontStyle: 'italic' }}>
                Thus concludes this magnificent tale, Your Majesty
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>
    </Fade>
  );
}

export default StoryScene;

